import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tv, Copy, Download, ExternalLink, CheckCircle2, Radio } from "lucide-react";

const htmlFor = (url) => `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>USC - Visuel TV</title>
<style>html,body{margin:0;height:100%;background:#000;overflow:hidden}iframe{position:fixed;inset:0;width:100%;height:100%;border:0}</style>
</head>
<body>
<iframe src="${url}" allow="autoplay; fullscreen" allowfullscreen></iframe>
</body>
</html>
`;

export const TvShareDialog = ({ bannerId }) => {
  const [open, setOpen] = useState(false);
  const [tvId, setTvId] = useState(null);
  const url = `${window.location.origin}/tv/${bannerId}`;
  const liteUrl = `${window.location.origin}/api/tv-lite?id=${bannerId}`;
  useEffect(() => { if (open) api.get("/settings/tv").then(({ data }) => setTvId(data.banner_id)).catch(() => {}); }, [open]);
  const makeDefault = async () => {
    try { await api.put("/settings/tv", { banner_id: bannerId }); setTvId(bannerId); toast.success("Ce bandeau est diffusé sur /usc/visueltv"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action réservée à l'administrateur principal"); }
  };
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Copie impossible"); }
  };
  const downloadHtml = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([htmlFor(url)], { type: "text/html" }));
    a.download = "index.html";
    a.click();
  };
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} data-testid="tv-share-button">
        <Tv className="mr-1 h-4 w-4" /> Diffusion TV
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="tv-share-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Diffuser ce bandeau sur une TV / un site</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Lien public en lecture seule (sans connexion), plein écran, mis à jour automatiquement chaque minute après vos modifications.
          </p>
          <div className="flex gap-2">
            <Input readOnly value={url} data-testid="tv-url-input" />
            <Button size="icon" variant="secondary" onClick={() => copy(url, "Lien copié")} data-testid="tv-copy-url"><Copy className="h-4 w-4" /></Button>
            <Button size="icon" variant="secondary" onClick={() => window.open(url, "_blank")} data-testid="tv-open-url"><ExternalLink className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1.5" data-testid="tv-lite-box">
            <p className="text-xs font-semibold">Version TV ancienne (navigateur ancien, ex. Toshiba)</p>
            <p className="text-[11px] text-muted-foreground">Page ultra-légère avec fondu simple. Les affiches y sont montrées en image (générée à chaque enregistrement de l'affiche). Sans « ?id=… », elle diffuse le bandeau de /usc/visueltv.</p>
            <div className="flex gap-2">
              <Input readOnly value={liteUrl} data-testid="tv-lite-url-input" />
              <Button size="icon" variant="secondary" onClick={() => copy(liteUrl, "Lien TV ancienne copié")} data-testid="tv-lite-copy-url"><Copy className="h-4 w-4" /></Button>
              <Button size="icon" variant="secondary" onClick={() => window.open(liteUrl, "_blank")} data-testid="tv-lite-open-url"><ExternalLink className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="space-y-2 rounded-lg border border-primary/40 bg-primary/5 p-3 text-xs" data-testid="tv-default-box">
            <p className="font-semibold">Adresse fixe : www.angodel.be/usc/visueltv</p>
            <p className="text-muted-foreground">Votre domaine angodel.be pointe directement sur l'application : cette adresse diffuse le bandeau choisi ici, sans fichier à déposer.</p>
            {tvId === bannerId ? (
              <p className="flex items-center gap-1 font-semibold text-primary" data-testid="tv-default-active"><CheckCircle2 className="h-4 w-4" /> Ce bandeau est actuellement diffusé sur /usc/visueltv</p>
            ) : (
              <Button size="sm" onClick={makeDefault} data-testid="tv-make-default"><Radio className="mr-1 h-4 w-4" /> Diffuser ce bandeau sur /usc/visueltv</Button>
            )}
          </div>
          <div className="space-y-2 rounded-lg border border-border p-3 text-xs">
            <p className="font-semibold">Autre site (hébergement séparé) :</p>
            <ol className="list-decimal space-y-1 pl-4 text-muted-foreground">
              <li>Téléchargez le fichier <b>index.html</b> ci-dessous.</li>
              <li>Sur votre hébergement angodel.be (FTP ou gestionnaire de fichiers), créez le dossier <b>/usc/visueltv/</b>.</li>
              <li>Déposez-y index.html : l'adresse www.angodel.be/usc/visueltv affichera le bandeau en plein écran.</li>
            </ol>
                        <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={downloadHtml} data-testid="tv-download-html"><Download className="mr-1 h-4 w-4" /> Télécharger index.html</Button>
              <Button size="sm" variant="ghost" onClick={() => copy(htmlFor(url), "Code HTML copié")} data-testid="tv-copy-html"><Copy className="mr-1 h-4 w-4" /> Copier le code</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
